import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import {
  drawingObjectState,
  drawingObjectWorldBounds
} from './drawing_object_registry.js?v=20260904f';

const DEBUG_GROUPS = false;
// Only immutable snapshot facts are reusable. Adapter fields (including active,
// rotation and membership) may change without a project revision.
const snapshotGroupCaches = new WeakMap();

function groupCacheForSnapshot(snapshot) {
  if (snapshot?.complete !== true || !Object.isFrozen(snapshot)) return null;
  let cache = snapshotGroupCaches.get(snapshot);
  if (!cache) {
    cache = { objects: new Map(), groups: new Map() };
    snapshotGroupCaches.set(snapshot, cache);
  }
  return cache;
}

/**
 * Core-Gruppenvertrag fuer 2D/3D/BOM/Sidebar:
 *
 * Eine Core-Gruppe ist die kleinste globale Bearbeitungseinheit. Jedes in die
 * Zeichnung eingefuegte Produktobjekt ist ab Einfuegen eine Gruppe; nach einem
 * systemspezifischen Merge/Snap repraesentiert genau eine Core-Gruppe die
 * verbundene Struktur.
 *
 * Systemadapter liefern ausschliesslich den neutralen Gruppenvertrag:
 * - Erforderlich: `id` oder `groupId`.
 * - Der Owner wird immer aus dem registrierten System abgeleitet. Ein Adapter
 *   darf weder einen fremden Owner noch einen nicht kanonischen Key deklarieren.
 * - Zugehoerige IDs werden neutral als `memberIds`, `objectIds`, `bomIds` und
 *   `renderIds` geliefert. Der Core interpretiert keine produktiven ID-Felder.
 * - `bounds` und `rotationPivot` werden vom Core aus den zugeordneten
 *   DrawingObjects abgeleitet, sobald diese verfuegbar sind. Systemspezifische
 *   Werte sind nur Fallbacks fuer noch nicht migrierte Objektarten.
 * - `active` und `rotationDeg` bleiben systemspezifische Laufzeitinformationen.
 * - `getDrawingObjects()` muss DrawingObjects mit `metadata.groupId` und
 *   stabilen `metadata.localBounds` liefern, damit Core-Funktionen wie der
 *   2D-Drehbutton automatisch auf der ganzen Gruppe arbeiten koennen.
 *   Fehlt `getCoreGroups()`, erzeugt der Core aus DrawingObjects mit explizitem
 *   neutralem `metadata.coreGroupId` oder `metadata.groupId` automatisch eine
 *   Gruppe. `metadata.coreGroup: false` nimmt ein Objekt explizit davon aus.
 *
 * Der Core normalisiert daraus Labels, Bounds, Scope-IDs und owner-gescopte
 * stabile Keys. Produktive Bedeutungen bleiben vollstaendig im Systemadapter.
 *
 * Wichtig: Systeme definieren nur Gruppenzugehoerigkeit und Merge-Regeln. Der
 * Drehbutton in der 2D-Zeichnungsansicht wird immer vom Core gerendert und ruft
 * die globale Core-Rotation auf; neue Systeme duerfen dafuer keine eigene
 * Buttonlogik brauchen.
 * Sind mehrere Core-Gruppen aktiv, behandelt der Core sie fuer die Rotation als
 * temporaere Gruppe und nutzt den Mittelpunkt der gemeinsamen Auswahl-Bounds.
 */
function getCoreGroups() {
  const groups = collectRegistryCoreGroups();
  if (DEBUG_GROUPS) console.debug('[groups/core]', groups);
  return groups;
}

function getActiveCoreGroups() {
  return getCoreGroups().filter(group => group.active);
}

function getSingleActiveCoreGroup() {
  const groups = getActiveCoreGroups();
  return groups.length === 1 ? groups[0] : null;
}

function getCoreGroupSidebarActions(group, context = {}) {
  if (!group || typeof group !== 'object') return [];
  return SystemRegistry.getCoreGroupSidebarActions(
    group.systemId || group.systemKey || '',
    group,
    context
  );
}

function executeCoreGroupSidebarAction(group, actionId, context = {}) {
  if (!group || typeof group !== 'object') return { handled: false, reason: 'missing-group' };
  return SystemRegistry.executeCoreGroupSidebarAction(
    group.systemId || group.systemKey || '',
    group,
    actionId,
    context
  );
}

function collectRegistryCoreGroups() {
  const groups = [];
  const drawingSnapshot = SystemRegistry.collectDrawingObjectSnapshot();
  for (const system of SystemRegistry.allSystems()) {
    if (typeof system?.getCoreGroups !== 'function') continue;
    let systemGroups;
    try {
      systemGroups = system.getCoreGroups() || [];
    } catch (error) {
      if (DEBUG_GROUPS) console.warn('[groups/core] Systemgruppen konnten nicht gelesen werden', system?.id, error);
      continue;
    }
    if (!Array.isArray(systemGroups)) continue;
    let groupCount;
    try {
      groupCount = systemGroups.length;
    } catch (error) {
      if (DEBUG_GROUPS) console.warn('[groups/core] Systemgruppenliste konnte nicht gelesen werden', system?.id, error);
      continue;
    }
    for (let index = 0; index < groupCount; index += 1) {
      try {
        const rawGroup = systemGroups[index];
        const group = normalizeCoreGroup(rawGroup, system, drawingSnapshot);
        if (group) groups.push(group);
      } catch (error) {
        if (DEBUG_GROUPS) console.warn('[groups/core] Systemgruppe konnte nicht normalisiert werden', system?.id, error);
      }
    }
  }
  return mergeCoreGroups(groups.concat(inferCoreGroupsFromDrawingObjects(groups, drawingSnapshot)));
}

function normalizedText(value) {
  return String(value ?? '').trim();
}

function canonicalCoreGroupKey(systemId, groupId) {
  const owner = normalizedText(systemId);
  const id = normalizedText(groupId);
  return owner && id ? `${owner}:${id}` : '';
}

function declaredCoreGroupOwnerMatches(rawGroup, systemId) {
  return ['systemId', 'systemKey', 'system', 'owner', 'ownerId']
    .every(field => !normalizedText(rawGroup?.[field]) || normalizedText(rawGroup[field]) === systemId);
}

function normalizeCoreGroupMeta(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return { ...value };
}

function normalizeCoreGroup(rawGroup, system, drawingSnapshot = null) {
  if (!rawGroup || typeof rawGroup !== 'object') return null;
  const registeredSystem = SystemRegistry.getSystem(normalizedText(system?.id));
  const systemId = normalizedText(registeredSystem?.id);
  if (!systemId || !declaredCoreGroupOwnerMatches(rawGroup, systemId)) return null;
  const groupId = normalizedText(rawGroup.groupId || rawGroup.id);
  if (!systemId || !groupId) return null;
  const key = canonicalCoreGroupKey(systemId, groupId);
  if (normalizedText(rawGroup.key) && normalizedText(rawGroup.key) !== key) return null;
  const explicitObjectIds = normalizeGroupIds(rawGroup.objectIds);
  const explicitBomIds = normalizeGroupIds(rawGroup.bomIds);
  const explicitRenderIds = normalizeGroupIds(rawGroup.renderIds);
  const primaryObjectId = normalizedText(rawGroup.primaryObjectId);
  const memberIds = normalizeGroupIds([
    rawGroup.memberIds,
    explicitObjectIds,
    explicitBomIds,
    explicitRenderIds,
    primaryObjectId
  ]);
  const objectIds = explicitObjectIds.length ? explicitObjectIds : memberIds;
  const fallbackBounds = normalizeGroupBounds(rawGroup.bounds || rawGroup.planBounds || rawGroup.rotationBounds || rawGroup.bounds2D);
  const sourceId = normalizedText(rawGroup.sourceId || groupId);
  const systemLabel = normalizedText(registeredSystem?.label || registeredSystem?.name || systemId);
  const displayName = normalizedText(
    rawGroup.displayName
      || rawGroup.label
      || rawGroup.name
      || rawGroup.groupTitleBase
      || systemLabel
      || groupId
  );
  const group = {
    id: groupId,
    groupId,
    key,
    displayName,
    label: normalizedText(rawGroup.label || displayName),
    name: normalizedText(rawGroup.name || displayName),
    systemLabel,
    systemKey: systemId,
    systemId,
    memberIds,
    objectIds: normalizeGroupIds(objectIds),
    bomIds: explicitBomIds,
    renderIds: explicitRenderIds,
    sourceId,
    primaryObjectId: primaryObjectId || objectIds[0] || memberIds[0] || sourceId,
    bomScopeId: key,
    omitFromBom: rawGroup.omitFromBom === true,
    bounds: fallbackBounds,
    rotationDeg: null,
    active: rawGroup.active === true,
    sidebarMeta: normalizedText(rawGroup.sidebarMeta || rawGroup.metaLabel || rawGroup.detailLabel),
    meta: normalizeCoreGroupMeta(rawGroup.meta || rawGroup.metadata)
  };
  // A missing adapter angle is not an explicit 0°. Read the persisted
  // reference object's orientation, also for groups with perpendicular members.
  const explicitRotation = rawGroup.rotationDeg;
  group.rotationDeg = explicitRotation != null && explicitRotation !== '' && Number.isFinite(Number(explicitRotation))
    ? Number(explicitRotation)
    : drawingObjectCoreRotationForGroup(group, drawingSnapshot);
  const coreObjectBounds = drawingObjectCoreBoundsForGroup(group, drawingSnapshot);
  const bounds = coreObjectBounds || fallbackBounds;
  return {
    ...group,
    bounds,
    rotationPivot: boundsCenter(bounds) || finitePoint(rawGroup.rotationPivot || rawGroup.pivot) || null
  };
}

function normalizeGroupIds(values = []) {
  const ids = [];
  const seen = new Set();
  const add = value => {
    if (Array.isArray(value)) {
      value.forEach(add);
      return;
    }
    if (value == null || value === '') return;
    const id = String(value).trim();
    if (id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  };
  add(values);
  return ids;
}

function normalizeGroupBounds(bounds) {
  const minX = Number(bounds?.minX);
  const minZ = Number(bounds?.minZ);
  const maxX = Number(bounds?.maxX);
  const maxZ = Number(bounds?.maxZ);
  if (![minX, minZ, maxX, maxZ].every(Number.isFinite)) return null;
  return {
    minX: Math.min(minX, maxX),
    minZ: Math.min(minZ, maxZ),
    maxX: Math.max(minX, maxX),
    maxZ: Math.max(minZ, maxZ)
  };
}

function boundsCenter(bounds) {
  const normalized = normalizeGroupBounds(bounds);
  return normalized
    ? {
        x: (normalized.minX + normalized.maxX) / 2,
        z: (normalized.minZ + normalized.maxZ) / 2
      }
    : null;
}

function finitePoint(point = null) {
  const x = Number(point?.x);
  const z = Number(point?.z ?? point?.y);
  return Number.isFinite(x) && Number.isFinite(z) ? { x, z } : null;
}

function drawingObjectSnapshotKey(object = {}) {
  const systemId = normalizedText(object?.systemType || object?.systemId);
  const objectId = normalizedText(object?.id);
  return systemId && objectId ? `${systemId}:${objectId}` : '';
}

function snapshotDrawingObjectState(object = {}, drawingSnapshot = null) {
  return drawingSnapshot?.statesByObject?.get?.(object)
    || drawingSnapshot?.statesByKey?.get(drawingObjectSnapshotKey(object))
    || drawingSnapshot?.statesById?.get(String(object?.id || ''))
    || drawingObjectState(object);
}

function snapshotDrawingObjectBounds(object = {}, drawingSnapshot = null) {
  return drawingSnapshot?.boundsByObject?.get?.(object)
    || drawingSnapshot?.boundsByKey?.get(drawingObjectSnapshotKey(object))
    || drawingSnapshot?.boundsById?.get(String(object?.id || ''))
    || null;
}

function coreGroupObjectInfo(object, drawingSnapshot) {
  if (!object) return null;
  const cache = groupCacheForSnapshot(drawingSnapshot);
  if (cache?.objects.has(object)) return cache.objects.get(object);
  const state = snapshotDrawingObjectState(object, drawingSnapshot);
  const metadata = state?.metadata || {};
  const info = state ? {
    state,
    metadata,
    systemId: normalizedText(state.systemType),
    sourceId: normalizedText(metadata.sourceId || ''),
    groupIds: normalizeGroupIds([
      metadata.coreGroupId, metadata.connectedGroupId, metadata.parentGroupId,
      metadata.groupId, metadata.sourceGroupId
    ]),
    coverageIds: normalizeGroupIds([
      metadata.sourceId, metadata.coreGroupId, metadata.groupId,
      metadata.connectedGroupId, metadata.parentGroupId, metadata.sourceGroupId
    ]),
    groupId: normalizedText(metadata.coreGroupId || metadata.groupId
      || metadata.connectedGroupId || metadata.parentGroupId || metadata.sourceGroupId || ''),
    optedOut: metadata.coreGroup === false || metadata.omitFromCoreGroups === true
  } : null;
  if (cache) cache.objects.set(object, info);
  return info;
}

function drawingObjectBounds(object = {}, drawingSnapshot = null) {
  const info = coreGroupObjectInfo(object, drawingSnapshot);
  const state = info?.state;
  if (!state) return null;
  if ('bounds' in info) return info.bounds;
  info.bounds = normalizeGroupBounds(snapshotDrawingObjectBounds(object, drawingSnapshot))
    || normalizeGroupBounds(drawingObjectWorldBounds(state))
    || normalizeGroupBounds(
      state?.metadata?.bounds
        || state?.metadata?.localBounds
    )
    || (Number.isFinite(Number(state?.position?.x)) && Number.isFinite(Number(state?.position?.z))
    ? {
        minX: Number(state.position.x),
        maxX: Number(state.position.x),
        minZ: Number(state.position.z),
        maxZ: Number(state.position.z)
      }
    : null);
  return info.bounds;
}

function drawingObjectCoreRotationForGroup(group, drawingSnapshot) {
  const snapshot = drawingSnapshot || SystemRegistry.collectDrawingObjectSnapshot();
  const objects = resolveCoreGroupDrawingObjects(group, snapshot).objects;
  const states = objects.map(object => coreGroupObjectInfo(object, snapshot)?.state).filter(Boolean);
  const primary = states.find(state => String(state.id) === group.primaryObjectId)
    || states.find(state => String(state.metadata?.sourceId || '') === group.primaryObjectId)
    || states[0];
  return Number(primary?.rotation) || 0;
}

function drawingObjectCoreBoundsForGroup(group = {}, drawingSnapshot = null) {
  const snapshot = drawingSnapshot || SystemRegistry.collectDrawingObjectSnapshot();
  const result = resolveCoreGroupDrawingObjects(group, snapshot);
  if (!('bounds' in result)) {
    result.bounds = result.objects.reduce(
      (bounds, object) => mergeGroupBounds(bounds, drawingObjectBounds(object, snapshot)), null
    );
  }
  return result.bounds ? { ...result.bounds } : null;
}

function mergeGroupBounds(a, b) {
  const left = normalizeGroupBounds(a);
  const right = normalizeGroupBounds(b);
  if (!left) return right;
  if (!right) return left;
  return {
    minX: Math.min(left.minX, right.minX),
    maxX: Math.max(left.maxX, right.maxX),
    minZ: Math.min(left.minZ, right.minZ),
    maxZ: Math.max(left.maxZ, right.maxZ)
  };
}

function coreGroupMemberIds(group) {
  return normalizeGroupIds([
    group.memberIds,
    group.objectIds,
    group.bomIds,
    group.renderIds,
    group.primaryObjectId,
    group.sourceId,
    group.groupId,
    group.id
  ]);
}

function inferCoreGroupsFromDrawingObjects(existingGroups = [], drawingSnapshot = null) {
  const groupsByKey = new Map();
  const coveredIdsByOwner = new Map();
  for (const group of existingGroups) {
    const owner = normalizedText(group.systemId || group.systemKey);
    let coveredIds = coveredIdsByOwner.get(owner);
    if (!coveredIds) {
      coveredIds = new Set();
      coveredIdsByOwner.set(owner, coveredIds);
    }
    for (const id of coreGroupMemberIds(group)) coveredIds.add(id);
  }
  const drawingObjects = drawingSnapshot?.objects || [];
  for (const object of drawingObjects) {
    const info = coreGroupObjectInfo(object, drawingSnapshot);
    if (!info || info.optedOut) continue;
    const { state, systemId, groupId, metadata } = info;
    const coveredIds = coveredIdsByOwner.get(systemId);
    if (coveredIds && (coveredIds.has(state.id) || info.coverageIds.some(id => coveredIds.has(id)))) continue;
    const system = SystemRegistry.getSystem(systemId);
    if (!system || !groupId) continue;
    const key = canonicalCoreGroupKey(system.id, groupId);
    const label = normalizedText(metadata.groupLabel || metadata.label || system.label || system.name || groupId);
    const entry = groupsByKey.get(key) || {
      id: groupId,
      groupId,
      key,
      systemId: system.id,
      systemKey: system.id,
      systemLabel: normalizedText(system.label || system.name || system.id),
      name: label,
      label,
      displayName: label,
      memberIds: [],
      objectIds: [],
      bomIds: [],
      renderIds: [],
      sourceId: groupId,
      primaryObjectId: state.id,
      bomScopeId: key,
      omitFromBom: metadata.omitFromBom === true,
      bounds: null,
      rotationDeg: Number(state?.rotation) || 0,
      rotationPivot: null,
      active: false,
      sidebarMeta: normalizedText(metadata.groupMeta || metadata.sidebarMeta),
      meta: {}
    };
    entry.memberIds.push(...normalizeGroupIds([metadata.memberIds, metadata.sourceId, state.id]));
    entry.objectIds.push(state.id);
    entry.bomIds.push(...normalizeGroupIds(metadata.bomIds || metadata.bomId));
    entry.renderIds.push(...normalizeGroupIds(metadata.renderIds || metadata.renderId));
    entry.bounds = mergeGroupBounds(entry.bounds, drawingObjectBounds(object, drawingSnapshot));
    groupsByKey.set(key, entry);
  }
  return [...groupsByKey.values()].map(group => ({
    ...group,
    memberIds: normalizeGroupIds(group.memberIds),
    objectIds: normalizeGroupIds(group.objectIds),
    bomIds: normalizeGroupIds(group.bomIds),
    renderIds: normalizeGroupIds(group.renderIds),
    rotationPivot: group.bounds
      ? {
          x: (group.bounds.minX + group.bounds.maxX) / 2,
          z: (group.bounds.minZ + group.bounds.maxZ) / 2
        }
      : null
  }));
}

function coreGroupToBomScope(group) {
  if (group?.omitFromBom === true) return null;
  if (!group?.systemId || !group?.bomScopeId || !group?.bounds) return null;
  const bomIds = normalizeGroupIds(group.bomIds);
  if (!bomIds.length) return null;
  return {
    id: group.bomScopeId,
    sourceId: group.sourceId || group.groupId,
    groupId: group.groupId,
    key: group.key,
    systemId: group.systemId,
    name: group.displayName || group.label || group.groupId,
    label: group.label || group.displayName || group.groupId,
    systemLabel: group.systemLabel || '',
    memberIds: normalizeGroupIds(group.memberIds),
    bomIds,
    objectIds: normalizeGroupIds(group.objectIds),
    renderIds: normalizeGroupIds(group.renderIds),
    bounds: group.bounds,
    meta: normalizeCoreGroupMeta(group.meta)
  };
}

function drawingObjectsForCoreGroup(group = {}, drawingSnapshot = null) {
  if (!normalizedText(group.systemId || group.systemKey)) return [];
  const snapshot = drawingSnapshot || SystemRegistry.collectDrawingObjectSnapshot();
  return resolveCoreGroupDrawingObjects(group, snapshot).objects.slice();
}

function resolveCoreGroupDrawingObjects(group, snapshot) {
  const systemId = String(group.systemId || group.systemKey || '').trim();
  if (!systemId) return { objects: [] };
  const objectIds = coreGroupMemberIds(group);
  const groupIdList = normalizeGroupIds([
    group.groupId,
    group.id,
    group.sourceId,
    group.key
  ]);
  const cache = groupCacheForSnapshot(snapshot);
  const key = canonicalCoreGroupKey(systemId, group.groupId || group.id);
  const signature = cache && key ? JSON.stringify([objectIds, groupIdList]) : '';
  const previous = cache?.groups.get(key);
  if (previous?.signature === signature) return previous;
  const objectIdSet = new Set(objectIds);
  const drawingIdSet = new Set(objectIds.flatMap(id => [id, `${systemId}:${id}`]));
  const groupIds = new Set(groupIdList);
  const indexedCandidates = new Set();
  for (const id of objectIds) {
    const compatibleObjectId = `${systemId}:${id}`;
    const directObjects = [
      snapshot.objectsByKey?.get(`${systemId}:${id}`),
      snapshot.objectsByKey?.get(`${systemId}:${compatibleObjectId}`)
    ];
    for (const object of directObjects) {
      if (coreGroupObjectInfo(object, snapshot)?.systemId === systemId) indexedCandidates.add(object);
    }
    for (const object of snapshot.objectsBySource?.get(`${systemId}:${id}`) || []) indexedCandidates.add(object);
  }
  for (const groupId of groupIds) {
    for (const object of snapshot.objectsByGroup?.get(`${systemId}:${groupId}`) || []) indexedCandidates.add(object);
  }
  const candidates = indexedCandidates.size
    ? [...indexedCandidates]
    : snapshot.objectsBySystem?.get(systemId) || [];
  const objects = candidates.filter(object => {
    const info = coreGroupObjectInfo(object, snapshot);
    if (!info || info.systemId !== systemId) return false;
    const { state, sourceId } = info;
    if (drawingIdSet.has(state.id)) return true;
    if (sourceId && objectIdSet.has(sourceId)) return true;
    return info.groupIds.some(id => groupIds.has(id));
  });
  const result = { signature, objects };
  // Retain only the latest membership for each group in this snapshot.
  if (cache && key) cache.groups.set(key, result);
  return result;
}

function collectCoreBomScopes() {
  return getCoreGroups().map(coreGroupToBomScope).filter(Boolean);
}

function mergeCoreGroups(groups = []) {
  const byKey = new Map();
  for (const group of groups) {
    const systemId = normalizedText(group?.systemId || group?.systemKey);
    const groupId = normalizedText(group?.groupId || group?.id);
    const key = canonicalCoreGroupKey(systemId, groupId);
    if (!key
      || !declaredCoreGroupOwnerMatches(group, systemId)
      || (normalizedText(group?.key) && normalizedText(group.key) !== key)
      || byKey.has(key)) continue;
    byKey.set(key, { ...group, id: groupId, groupId, key, systemId, systemKey: systemId });
  }
  return [...byKey.values()];
}

export {
  getCoreGroups,
  getActiveCoreGroups,
  getSingleActiveCoreGroup,
  getCoreGroupSidebarActions,
  executeCoreGroupSidebarAction,
  collectRegistryCoreGroups,
  canonicalCoreGroupKey,
  normalizeCoreGroup,
  mergeCoreGroups,
  normalizeGroupIds,
  drawingObjectsForCoreGroup,
  coreGroupToBomScope,
  collectCoreBomScopes
};
